import {
  LayoutDashboard,
  CalendarHeart,
  Users,
  BookText,
  Plus,
  CalendarDays,
  Settings,
  Moon,
  ChevronLeft,
  ChevronUp,
  ChevronRight,
  MousePointerClick,
  Sparkles,
  BrainCircuit,
  MessagesSquare,
  Share2,
  ListChecks,
  PartyPopper,
} from "lucide-react";
import { TOUR_STEPS } from "@/components/tour/tourSteps";

const ICON_MAP = {
  LayoutDashboard, CalendarHeart, Users, BookText, Plus, CalendarDays, Settings, Moon,
  Sparkles, BrainCircuit, MessagesSquare, Share2, ListChecks, PartyPopper,
};

export default function TourStep({ step, stepIndex, language, onSkip, onNext, style }) {
  const en = language === "en";
  const copy = en ? step.en : step.it;
  const Icon = ICON_MAP[step.icon] || LayoutDashboard;
  const isFixedCorner = step.cardPosition === "fixed-corner";
  const manualNext = step.advanceOn?.type === "next";

  // Arrow: points toward the target element from the card edge nearest to it.
  // Not shown for fixed-corner cards (no specific element to point at).
  const ArrowIcon = step.cardPosition === "below" ? ChevronUp : ChevronLeft;
  const arrowClass = step.cardPosition === "below" ? "tour-arrow-down" : "tour-arrow-right";
  const arrowEdgeClass = step.cardPosition === "below"
    ? "absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full pt-1"
    : "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full pr-1";

  return (
    <div
      className="fixed z-[10000] w-[min(18rem,calc(100vw-2rem))] bg-card border border-border rounded-sm shadow-xl p-5 flex flex-col gap-3"
      style={style}
      data-testid={`tour-step-${step.id}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-title-${step.id}`}
      aria-describedby={`tour-body-${step.id}`}
    >
      {/* Blinking directional arrow — hidden for fixed-corner (form steps) */}
      {!isFixedCorner && (
        <div className={`${arrowEdgeClass} pointer-events-none`}>
          <ArrowIcon className={`w-6 h-6 text-primary ${arrowClass}`} strokeWidth={2} />
        </div>
      )}

      {/* Progress counter + dots */}
      <div className="flex items-center justify-between">
        <span className="label-eyebrow text-muted-foreground">
          {en
            ? `Step ${stepIndex + 1} of ${TOUR_STEPS.length}`
            : `Passo ${stepIndex + 1} di ${TOUR_STEPS.length}`}
        </span>
        <div className="flex gap-1">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === stepIndex ? "w-4 bg-primary" : "w-1.5 bg-border"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step icon */}
      <div className="flex items-center justify-center h-14 bg-secondary/40 rounded-sm border border-border/60">
        <Icon className="w-7 h-7 text-primary/70" strokeWidth={1.2} />
      </div>

      {/* Copy */}
      <div className="space-y-1">
        <h3 id={`tour-title-${step.id}`} className="serif text-lg font-light leading-tight">{copy.title}</h3>
        <p id={`tour-body-${step.id}`} className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
      </div>

      {/* CTA — tells the user exactly what action to take */}
      {copy.cta && (
        <div className="flex items-center gap-1.5 text-primary text-xs font-semibold pt-0.5">
          <MousePointerClick className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
          <span>{copy.cta}</span>
        </div>
      )}

      {/* "Avanti" — only for informational steps that advance manually
          (no element to click / route to follow). */}
      {manualNext && (
        <button
          onClick={onNext}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-sm bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-wider py-2 hover:bg-primary/90 transition-colors"
          data-testid="tour-next-btn"
        >
          {copy.next || (en ? "Next" : "Avanti")}
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}

      {/* Skip — always present, the only way to exit the tour */}
      <div className="pt-1 border-t border-border/50">
        <button
          onClick={onSkip}
          className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center py-1"
          data-testid="tour-skip-btn"
        >
          {en ? "Skip tour" : "Salta il tutorial"}
        </button>
      </div>
    </div>
  );
}
